<!-- Hero Section -->
<section class="linux-cloud-hero">
  <div class="container">
    <div class="row align-items-center">
      <div class="col-lg-6">
        <div class="hero-content">
          <span class="hero-badge">
            <i class="fab fa-linux"></i>
            Linux Cloud Infrastructure
          </span>
          <h1 class="hero-title">
            High-Performance <span class="text-gradient">Linux Cloud</span> Solutions
          </h1>
          <p class="hero-description">
            Deploy enterprise-grade Linux instances in seconds. Optimized for performance, security, and scalability with full root access.
          </p>
          
          <div class="hero-actions">
            <a class="btn-primary-gradient me-3" routerLink="/contact">
              <i class="bx bx-rocket"></i>
              Deploy Server
            </a>
            <a class="btn-outline-light" routerLink="/contact">
              <i class="bx bx-phone"></i>
              Talk to Expert
            </a>
          </div>

          <div class="hero-features">
            <div class="feature-item">
              <i class="bx bx-check-shield"></i>
              <span>Enterprise Security</span>
            </div>
            <div class="feature-item">
              <i class="bx bx-time-five"></i>
              <span>99.9% Uptime SLA</span>
            </div>
            <div class="feature-item">
              <i class="bx bx-support"></i>
              <span>24/7 Expert Support</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="hero-image">
          <div class="image-decoration">
            <img src="assets/img/linux-cloud-hero.png" alt="Linux Cloud Infrastructure" class="main-image">
            
            <!-- Floating Cards -->
            <div class="floating-card card-1">
              <i class="fab fa-ubuntu text-orange-500"></i>
              <span>Ubuntu 22.04 LTS</span>
            </div>
            <div class="floating-card card-2">
              <i class="fab fa-docker text-blue-500"></i>
              <span>Docker Ready</span>
            </div>
            <div class="floating-card card-3">
              <i class="bx bx-terminal text-green-500"></i>
              <span>Full Root Access</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <div class="hero-background-elements">
    <div class="bg-element element-1"></div>
    <div class="bg-element element-2"></div>
    <div class="bg-element element-3"></div>
  </div>
</section>

<!-- Features Section -->
<section class="features-section ptb-100">
  <div class="container">
    <div class="section-title">
      <span class="section-badge">Enterprise Features</span>
      <h2>Linux Cloud Capabilities</h2>
      <p>
        Enterprise-grade Linux instances with comprehensive features designed for high-performance applications, container deployments, and scalable web infrastructure.
      </p>
    </div>

    <div class="row">
      <!-- Full Root Access -->
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card advanced">
          <div class="feature-header">
            <div class="feature-icon">
              <i class="bx bx-terminal"></i>
            </div>
            <span class="feature-badge">Control</span>
          </div>
          <h4>Full Root Access</h4>
          <p>
            Complete administrative control over your Linux environment with sudo privileges, custom configurations, and unrestricted package installations.
          </p>
          <ul class="feature-list">
            <li>
              <i class="bx bx-check-circle"></i>
              <span>SSH root access</span>
            </li>
            <li>
              <i class="bx bx-check-circle"></i>
              <span>Sudo privileges</span>
            </li>
            <li>
              <i class="bx bx-check-circle"></i>
              <span>Custom kernel modules</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- High-Performance Infrastructure -->
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card advanced">
          <div class="feature-header">
            <div class="feature-icon">
              <i class="bx bx-tachometer"></i>
            </div>
            <span class="feature-badge">Speed</span>
          </div>
          <h4>High-Performance</h4>
          <p>
            NVMe SSD storage, high-speed networking, and latest-generation processors ensure optimal performance for demanding Linux workloads.
          </p>
          <ul class="feature-list">
            <li>
              <i class="bx bx-check-circle"></i>
              <span>NVMe SSD storage</span>
            </li>
            <li>
              <i class="bx bx-check-circle"></i>
              <span>Up to 10Gbps network</span>
            </li>
            <li>
              <i class="bx bx-check-circle"></i>
              <span>Intel Xeon processors</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Enterprise Security -->
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card advanced">
          <div class="feature-header">
            <div class="feature-icon">
              <i class="bx bx-shield-check"></i>
            </div>
            <span class="feature-badge">Secure</span>
          </div>
          <h4>Enterprise Security</h4>
          <p>
            Multi-layered security with iptables firewall, fail2ban protection, and automated security updates for comprehensive protection.
          </p>
          <ul class="feature-list">
            <li>
              <i class="bx bx-check-circle"></i>
              <span>Advanced firewall</span>
            </li>
            <li>
              <i class="bx bx-check-circle"></i>
              <span>Fail2ban protection</span>
            </li>
            <li>
              <i class="bx bx-check-circle"></i>
              <span>SSL/TLS encryption</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Linux Distributions Section -->
<section class="linux-distributions-section ptb-100">
  <div class="container">
    <div class="section-title">
      <span class="section-badge">Linux Distributions</span>
      <h2>Choose Your Linux Distribution</h2>
      <p>
        Select from popular Linux distributions, each optimized for different workloads and use cases with full package management and community support.
      </p>
    </div>

    <div class="row mb-5">
      <!-- Ubuntu Server -->
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="distro-card featured">
          <div class="distro-header">
            <div class="distro-icon">
              <i class="fab fa-ubuntu"></i>
            </div>
            <span class="distro-badge">Popular</span>
          </div>
          <div class="distro-content">
            <h3>Ubuntu Server</h3>
            <div class="distro-version">20.04 LTS / 22.04 LTS</div>
            <p class="distro-description">
              The most popular Linux distribution for servers with long-term support, extensive package repositories, and excellent documentation.
            </p>
            <div class="distro-features">
              <h5>Key Features:</h5>
              <ul class="feature-list">
                <li><i class="bx bx-check"></i> 5-year LTS support</li>
                <li><i class="bx bx-check"></i> APT package manager</li>
                <li><i class="bx bx-check"></i> Snap package support</li>
              </ul>
            </div>
            <div class="distro-footer">
              <a routerLink="/contact" class="btn-select-distro">Select Ubuntu</a>
            </div>
          </div>
        </div>
      </div>

      <!-- CentOS Stream -->
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="distro-card">
          <div class="distro-header">
            <div class="distro-icon">
              <i class="fab fa-centos"></i>
            </div>
            <span class="distro-badge enterprise">Enterprise</span>
          </div>
          <div class="distro-content">
            <h3>CentOS Stream</h3>
            <div class="distro-version">Stream 8 / 9</div>
            <p class="distro-description">
              Enterprise-grade Linux distribution with robust security, stability, and compatibility with Red Hat Enterprise Linux ecosystem.
            </p>
            <div class="distro-features">
              <h5>Key Features:</h5>
              <ul class="feature-list">
                <li><i class="bx bx-check"></i> Enterprise stability</li>
                <li><i class="bx bx-check"></i> YUM/DNF manager</li>
                <li><i class="bx bx-check"></i> RHEL compatible</li>
              </ul>
            </div>
            <div class="distro-footer">
              <a routerLink="/contact" class="btn-select-distro outline">Select CentOS</a>
            </div>
          </div>
        </div>
      </div>

      <!-- Debian -->
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="distro-card">
          <div class="distro-header">
            <div class="distro-icon">
              <i class="fab fa-debian"></i>
            </div>
            <span class="distro-badge stable">Stable</span>
          </div>
          <div class="distro-content">
            <h3>Debian</h3>
            <div class="distro-version">11 (Bullseye) / 12 (Bookworm)</div>
            <p class="distro-description">
              Rock-solid stability and security with conservative release cycles, perfect for mission-critical applications and servers.
            </p>
            <div class="distro-features">
              <h5>Key Features:</h5>
              <ul class="feature-list">
                <li><i class="bx bx-check"></i> Maximum stability</li>
                <li><i class="bx bx-check"></i> APT package manager</li>
                <li><i class="bx bx-check"></i> Massive repository</li>
              </ul>
            </div>
            <div class="distro-footer">
              <a routerLink="/contact" class="btn-select-distro outline">Select Debian</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional Distributions -->
    <div class="additional-distros">
      <h4>More Distributions Available</h4>
      <div class="row">
        <div class="col-md-3 col-sm-6 mb-4">
          <div class="mini-distro">
            <i class="fab fa-redhat"></i>
            <h6>Rocky Linux</h6>
            <p>Enterprise Stability</p>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-4">
          <div class="mini-distro">
            <i class="fab fa-linux"></i>
            <h6>AlmaLinux</h6>
            <p>Community Enterprise</p>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-4">
          <div class="mini-distro">
            <i class="fab fa-fedora"></i>
            <h6>Fedora Server</h6>
            <p>Cutting-edge Tech</p>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-4">
          <div class="mini-distro">
            <i class="fab fa-suse"></i>
            <h6>openSUSE</h6>
            <p>YaST Admin Tool</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Use Cases Section -->
<section class="use-cases-section ptb-100">
  <div class="container">
    <div class="section-title">
      <span class="section-badge">Application Scenarios</span>
      <h2>Linux Cloud Use Cases</h2>
      <p>
        Discover how Linux Cloud infrastructure powers diverse applications, from high-traffic web services to containerized microservices and development environments.
      </p>
    </div>

    <div class="row">
      <!-- Web Applications & Hosting -->
      <div class="col-lg-6 mb-4">
        <div class="use-case-card primary">
          <div class="use-case-header">
            <div class="use-case-icon">
              <i class="bx bx-world"></i>
            </div>
            <span class="use-case-category">Web Hosting</span>
          </div>
          <h4>Web Applications & Hosting</h4>
          <p>
            Deploy high-performance web applications with NGINX, Apache, PHP, Python, Node.js, and popular frameworks like Laravel, Django, and Express.
          </p>
          <div class="use-case-features">
            <div class="row">
              <div class="col-md-6">
                <ul>
                  <li><i class="bx bx-check-circle"></i> LAMP/LEMP stack</li>
                  <li><i class="bx bx-check-circle"></i> Node.js runtime</li>
                </ul>
              </div>
              <div class="col-md-6">
                <ul>
                  <li><i class="bx bx-check-circle"></i> Python/Django</li>
                  <li><i class="bx bx-check-circle"></i> PHP 8.x support</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="use-case-stats">
            <div class="stat-item">
              <span class="stat-number">99.9%</span>
              <span class="stat-label">Uptime</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">&lt;50ms</span>
              <span class="stat-label">Latency</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Container & Microservices -->
      <div class="col-lg-6 mb-4">
        <div class="use-case-card secondary">
          <div class="use-case-header">
            <div class="use-case-icon">
              <i class="bx bxl-docker"></i>
            </div>
            <span class="use-case-category">DevOps</span>
          </div>
          <h4>Container & Microservices</h4>
          <p>
            Run containerized applications with Docker, Kubernetes orchestration, and microservices architecture for scalable modern applications.
          </p>
          <div class="use-case-features">
            <div class="row">
              <div class="col-md-6">
                <ul>
                  <li><i class="bx bx-check-circle"></i> Docker Engine</li>
                  <li><i class="bx bx-check-circle"></i> Kubernetes Ready</li>
                </ul>
              </div>
              <div class="col-md-6">
                <ul>
                  <li><i class="bx bx-check-circle"></i> CI/CD Integration</li>
                  <li><i class="bx bx-check-circle"></i> Microservices</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="use-case-stats">
            <div class="stat-item">
              <span class="stat-number">Auto</span>
              <span class="stat-label">Scaling</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">Multi</span>
              <span class="stat-label">Container</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="cta-section">
  <div class="container">
    <div class="cta-content-wrapper">
      <div class="row align-items-center">
        <div class="col-lg-8">
          <div class="cta-content">
            <h2>Ready to Deploy Linux Cloud Infrastructure?</h2>
            <p class="cta-description">
              Launch your Linux server instance in minutes with full root access,
              enterprise features, and 24/7 expert support. Experience the power and flexibility
              of open-source computing.
            </p>

            <div class="cta-features">
              <div class="feature-item">
                <i class="bx bx-time-five"></i>
                <span>Deploy Linux instances in under 30 seconds</span>
              </div>
              <div class="feature-item">
                <i class="bx bx-shield-quarter"></i>
                <span>99.9% uptime SLA with redundant infrastructure</span>
              </div>
              <div class="feature-item">
                <i class="bx bx-support"></i>
                <span>24/7 Linux-certified support engineers</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="cta-actions">
            <a routerLink="/contact" class="btn-cta-primary">
              <i class="bx bx-rocket"></i>
              <span>Launch Linux Cloud</span>
              <small>Start your deployment</small>
            </a>

            <a routerLink="/contact" class="btn-cta-secondary">
              <i class="bx bx-phone"></i>
              <span>Talk to Linux Expert</span>
              <small>Design the right architecture</small>
            </a>

            <div class="cta-guarantee">
              <i class="bx bx-shield-check"></i>
              <span>Secure, SLA-backed Linux infrastructure</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>